import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Icon from "./AppIcon";
import apiUtils from "utils/apiutils";

function SubscriptionTable() {
    const [transactions, setTransactions] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    // Full headers with all fields
    const headers = [
        { key: "serial", label: "S.No" },
        { key: "DateTime", label: "Date & Time" },
        { key: "TransID", label: "Trans ID" },
        { key: "Subscription", label: "Plan" },
        // { key: "Email", label: "Email" },
        { key: "Amount", label: "Amount ($)" },
        { key: "Validity", label: "Validity" },
        { key: "ExpiryDate", label: "Expiry Date" },
        { key: "Status", label: "Status" },
    ];

    const formatDate = (dateStr) => {
        try {
            return new Date(dateStr).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        } catch {
            return "-";
        }
    };

    const formatStatus = (status) => {
        return status ? (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
                Active
            </span>
        ) : (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-400">
                Inactive
            </span>
        );
    };

    const formatAmount = (amount) => {
        return amount === 0 ? "Free" : `$${amount}`;
    };

    const fetchTransactions = async () => {
        setIsLoading(true);
        const payload = { page_no: pageNo, page_size: pageSize };

        try {
            const resp = await apiUtils.post("/subscription/view_subscription_transaction", payload, {
                showToast: false,
            });

            if (resp?.Success && Array.isArray(resp.Success)) {
                setTransactions(resp.Success);
            } else {
                setTransactions([]);
            }
        } catch (e) {
            console.error("Error fetching transactions:", e);
            setTransactions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        const payload = { page_no: pageNo, page_size: pageSize };

        try {
            const resp = await apiUtils.post("/subscription/total_view_subscription_transaction", payload, {
                showToast: false,
            });
            if (resp?.Success && typeof resp.Success === "number") {
                setTotalPages(Math.ceil(resp.Success / pageSize));
            }
        } catch (e) {
            console.error("Error fetching total count:", e);
        }
    };

    useEffect(() => {
        fetchTransactions();
        fetchTotalCount();
    }, [pageNo, pageSize]);

    const handlePrevPage = () => {
        if (pageNo > 1) setPageNo(pageNo - 1);
    };

    const handleNextPage = () => {
        if (pageNo < totalPages) setPageNo(pageNo + 1);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Icon name="Loader" size={32} className="animate-spin text-ring" />
            </div>
        );
    }

    if (!transactions.length) {
        return (
            <div className="text-center py-16 text-muted-foreground">
                <Icon name="Inbox" size={56} className="mx-auto mb-4 opacity-30" />
                <p className="text-xl">No subscription transactions found.</p>
            </div>
        );
    }

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-primary rounded-xl overflow-hidden"
            >
                <div className="p-6 border-b border-primary">
                    <h2 className="text-2xl font-bold">Subscription Transactions</h2>

                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-xs uppercase bg-card/70 border-b border-primary">
                            <tr>
                                {headers.map((h) => (
                                    <th key={h.key} className="px-4 py-4 font-semibold text-foreground/70">
                                        {h.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((tx, index) => (
                                <motion.tr
                                    key={tx.TransID}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className="border-b border-primary/30 hover:bg-primary/5 transition text-sm"
                                >
                                    <td className="px-4 py-4 font-medium">
                                        {(pageNo - 1) * pageSize + index + 1}
                                    </td>
                                    <td className="px-4 py-4">{formatDate(tx.DateTime)}</td>
                                    <td className="px-4 py-4 font-mono text-primary">{tx.TransID}</td>
                                    <td className="px-4 py-4">
                                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">
                                            {tx.Subscription}
                                        </span>
                                    </td>
                                    {/* <td className="px-4 py-4">{tx.Email}</td> */}
                                    <td className="px-4 py-4 font-semibold">{formatAmount(tx.Amount)}</td>
                                    <td className="px-4 py-4 text-center font-medium">
                                        {tx.Validity} days
                                    </td>
                                    <td className="px-4 py-4">{formatDate(tx.ExpiryDate)}</td>
                                    <td className="px-4 py-4">{formatStatus(tx.Status)}</td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden p-4 space-y-4">
                    {transactions.map((tx, index) => (
                        <motion.div
                            key={tx.TransID}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-card/60 border border-primary rounded-xl p-5 shadow-md"
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <p className="font-mono text-primary text-sm">{tx.TransID}</p>
                                    <p className="text-xs text-muted-foreground">{formatDate(tx.DateTime)}</p>
                                </div>
                                {formatStatus(tx.Status)}
                            </div>
                            <div className="flex justify-between">
                                <span className="text-foreground/70">Plan</span>
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                                    {tx.Subscription}
                                </span>
                            </div>
                            <div className="space-y-2 text-sm">
                                {/* <div className="flex justify-between">
                                    <span className="text-foreground/70">Email</span>
                                    <span className="font-medium truncate max-w-[180px]">{tx.Email}</span>
                                </div> */}

                                <div className="flex justify-between">
                                    <span className="text-foreground/70">Amount</span>
                                    <span className="font-bold">{formatAmount(tx.Amount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-foreground/70">Days Left</span>
                                    <span className="font-medium">{tx.Validity} days</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-foreground/70">Expires</span>
                                    <span>{formatDate(tx.ExpiryDate)}</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-primary bg-card/50">
                    <button
                        onClick={handlePrevPage}
                        disabled={pageNo === 1}
                        className="px-5 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                        Previous
                    </button>

                    <span className="text-sm font-medium">
                        Page <span className="text-primary">{pageNo}</span> / {totalPages}
                    </span>

                    <button
                        onClick={handleNextPage}
                        disabled={pageNo === totalPages}
                        className="px-5 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
                    >
                        Next
                    </button>
                </div>
            </motion.div>
        </>
    );
}

export default SubscriptionTable;